import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import CustomerSchedule from 'components/CustomerSchedule'
import MainButton from 'components/Buttons/MainButton'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import CutileiLogo from 'icons/logo-cutilei-simple.png'

import {
  Container,
  SubContainer,
  LogoRow,
  SchedulesGrid,
  SmallImage,
  Title,
  InfoText,
  WarningText
} from './styles'

export default function ConfirmSchedule () {
  const { schedule_id } = useParams ()
  const [searchParams] = useSearchParams ()
  const [loading, setLoading] = useState (true)
  const [schedules, setSchedules] = useState ([])
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [schedule] = schedules
  const pendingSchedules = schedules.filter (s => s.status !== 'confirmed')
  const multipleSchedules = schedules.length > 1

  useEffect (() => {
    getData ()
  }, [])

  /*
    A mensagem de confirmação com vários agendamentos do dia manda o primeiro no caminho e os
    demais em `?schedules=`. Link sem o parâmetro, como os que já foram enviados, continua
    confirmando um só.

    Extra que não carrega (apagado, ID errado) fica de fora sem derrubar os outros. Só entra
    extra do mesmo cliente e do mesmo salão, e que não foi cancelado depois do envio.
  */
  const getData = async () => {
    const otherScheduleIds = [...new Set (
      (searchParams.get ('schedules') ?? '').split (',').filter (id => id && id !== schedule_id)
    )]

    try {
      const { data: schedule } = await cutileiApi.get (
        `/schedules/${schedule_id}`
      )

      const otherSchedules = await Promise.all (otherScheduleIds.map (id => (
        cutileiApi.get (`/schedules/${id}`)
          .then (({ data }) => data)
          .catch (error => {
            logApiError (error)
            return null
          })
      )))

      setSchedules ([
        schedule,
        ...otherSchedules.filter (s => (
          s && !s.canceled
          && s.customer?.id === schedule.customer?.id
          && s.business.id === schedule.business.id
        ))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const handleConfirmSchedules = async () => {
    try {
      const status = 'confirmed'

      setErrorMessage (null)

      await Promise.all (pendingSchedules.map (s => (
        cutileiApi.post (`/schedules/${s.id}/status`, { status })
      )))

      setSchedules (schedules.map (s => ({ ...s, status })))
      toggleOkDialog ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    }
  }

  const toggleConfirmDialog = () => setShowConfirmDialog (!showConfirmDialog)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Container>
      <LogoRow>
        <SmallImage src={CutileiLogo} alt='Cutilei'/>
        <Title style={{margin: 0}}>Cutilei</Title>
      </LogoRow>
      {loading ? (
        <Loading/>
      ) : !schedule ? (
        <SubContainer>
          <Title>Agendamento não encontrado</Title>
          <InfoText>
            Confira o link que você recebeu ou fale com o salão.
          </InfoText>
        </SubContainer>
      ) : (
        <SubContainer>
          <Title>Olá, {schedule.customer.name.split (' ')[0]}!</Title>
          <InfoText>
            {multipleSchedules
              ? 'Verifique os dados de seus agendamentos e confirme clicando no botão abaixo.'
              : 'Verifique os dados de seu agendamento e confirme clicando no botão abaixo.'}
          </InfoText>
          {pendingSchedules.length === 0 && (
            <Title style={{color: '#44CF6C'}}>
              {multipleSchedules ? 'Agendamentos confirmados' : 'Agendamento confirmado'}
            </Title>
          )}
          {multipleSchedules ? (
            <SchedulesGrid total={schedules.length}>
              {schedules.map (s => (
                <CustomerSchedule
                  key={s.id}
                  data={s}
                  showActions={false}
                />
              ))}
            </SchedulesGrid>
          ) : (
            <CustomerSchedule
              data={schedule}
              showActions={false}
              containerStyle={{width: 360}}
            />
          )}
          {pendingSchedules.length > 0 && (
            <MainButton
              label={multipleSchedules ? 'Confirmar agendamentos' : 'Confirmar agendamento'}
              onClick={toggleConfirmDialog}
              style={{width: 200}}
            />
          )}
          {errorMessage && (
            <WarningText>{errorMessage}</WarningText>
          )}
          <AlertDialog
            visible={showConfirmDialog}
            title='Atenção!'
            message={multipleSchedules
              ? 'Deseja realmente confirmar estes agendamentos?'
              : 'Deseja realmente confirmar este agendamento?'}
            confirmText='Sim'
            cancelText='Não'
            onConfirm={handleConfirmSchedules}
            onClose={toggleConfirmDialog}
          />
          <AlertDialog
            visible={showOkDialog}
            title='Sucesso'
            message={multipleSchedules ? 'Agendamentos confirmados!' : 'Agendamento confirmado!'}
            confirmText='Ok'
            onConfirm={toggleOkDialog}
            onClose={toggleOkDialog}
          />
        </SubContainer>
      )}
    </Container>
  )
}
